import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Clock, ArrowRight, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

export const ProfileCountdownBanner = ({ userId }: { userId: string }) => {
  const [activeCampaign, setActiveCampaign] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) return;

    const fetchCampaign = async () => {
      if (window.location.search.includes('preview_recovery=true')) {
        setActiveCampaign({
           id: 'preview-123',
           current_state: '50_PERCENT_OFFER',
           discount_percentage: 50,
           required_additional_photos: 3,
           expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString()
        });
        return;
      }

      const { data } = await supabase
        .from("recovery_campaigns")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "ACTIVE")
        .in("current_state", ["50_PERCENT_OFFER", "30_PERCENT_OFFER"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data && data.expires_at) {
        if (new Date(data.expires_at) > new Date()) {
          setActiveCampaign(data);
        }
      }
    };

    fetchCampaign();
  }, [userId]);

  useEffect(() => {
    if (!activeCampaign?.expires_at) return;

    const interval = setInterval(() => {
      const now = new Date().getTime();
      const expiration = new Date(activeCampaign.expires_at).getTime();
      const distance = expiration - now;

      if (distance < 0) {
        clearInterval(interval);
        setTimeLeft(null);
        setActiveCampaign(null);
        return;
      }

      setTimeLeft({
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [activeCampaign]);

  if (!activeCampaign || !timeLeft) return null;

  const is50 = activeCampaign.current_state === "50_PERCENT_OFFER";

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="w-full bg-gradient-to-r from-[#FF0055] via-[#FF6A00] to-[#FF0055] p-[2px] relative z-50 shadow-[0_0_30px_rgba(255,106,0,0.4)]"
    >
      <div className="bg-black/90 backdrop-blur-md px-4 py-3 flex flex-col md:flex-row items-center justify-between gap-4 w-full">
        <div className="flex items-center gap-3">
          <div className="bg-[#FF6A00]/20 p-2 rounded-lg animate-pulse">
            <Flame className="w-5 h-5 text-[#FF6A00]" />
          </div>
          <div>
            <h4 className="text-white font-black uppercase tracking-widest text-sm flex items-center gap-2">
              <Tag className="w-4 h-4 text-[#FF0055]" />
              OFERTA DE {activeCampaign.discount_percentage}% ATIVA!
            </h4>
            <p className="text-white/60 text-xs font-medium">
              Adicione mais {activeCampaign.required_additional_photos} fotos no seu carrinho.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex gap-2 font-display text-xl font-black text-white">
            <div className="bg-white/10 px-2 py-1 rounded border border-white/20">
              {String(timeLeft.hours).padStart(2, "0")}
            </div>
            <span className="text-white/50 py-1">:</span>
            <div className="bg-white/10 px-2 py-1 rounded border border-white/20">
              {String(timeLeft.minutes).padStart(2, "0")}
            </div>
            <span className="text-white/50 py-1">:</span>
            <div className="bg-white/10 px-2 py-1 rounded border border-white/20 text-[#FF6A00] animate-pulse">
              {String(timeLeft.seconds).padStart(2, "0")}
            </div>
          </div>
          
          <Button 
            onClick={() => {
              navigate("/albums");
            }}
            className="bg-white text-black hover:bg-gray-200 font-black uppercase text-xs h-10 px-6 hidden sm:flex gap-2"
          >
            RESGATAR <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
